/**
 * MarketingSectionRewardsByAmount.tsx - Amount-Based Rewards Configuration
 * 
 * This component manages achievement-based rewards for promoters based on
 * donation amounts raised with tier management and validation.
 * 
 * Key Features:
 * - Amount-based reward tier configuration
 * - Reward level and description management
 * - Ascending amount validation and ordering
 * - Add, edit, and delete reward tiers
 * - Real-time validation and error feedback
 * - Preview of reward structure
 * 
 * Reward Configuration:
 * - Amount Level: Donation threshold for reward eligibility
 * - Reward Description: What promoters receive at this level
 * - Tier ordering with automatic sorting by amount
 * - Validation for logical progression
 * 
 * Validation Rules:
 * - Ascending amount requirements (no duplicates)
 * - Minimum amount thresholds
 * - Required reward descriptions
 * - Maximum number of reward tiers
 * 
 * Management Features:
 * - Inline editing with save/cancel options
 * - Add new tiers with validation
 * - Delete tiers with confirmation
 * - Automatic sorting by amount levels
 * - Error highlighting for invalid configurations
 * 
 * Props: None (uses MobX store for state management)
 * 
 * Dependencies: MobX store, amount validation, reward management
 */

import React, { useState } from 'react';
import styled from 'styled-components';
import { observer } from 'mobx-react-lite';
import { Check, X, Pencil, Trash2 } from 'lucide-react';
import { CreateFundraiserModel } from '../../../models';
import { RewardModel } from '../../../models/FundraisersGridModels';

const LevelsCaption = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #374151;
  margin-bottom: 16px;
`;

const AddLinksContainer = styled.div`
  display: flex;
  gap: 16px;
  margin-top: 16px;
`;

const AddLinkButton = styled.button<{ disabled?: boolean }>`
  background: none;
  border: none;
  color: ${props => props.disabled ? '#9ca3af' : '#3b82f6'};
  fontSize: 16px;
  fontWeight: 600;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  padding: 0;
  textDecoration: none;
  opacity: ${props => props.disabled ? 0.5 : 1};
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    text-decoration: underline;
  }
`;

const AchievementsTableHeader = styled.div`
  display: grid;
  grid-template-columns: 190px 1fr 80px;
  padding: 12px 16px;
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  marginBottom: 0px;
  width: 750px;
  fontSize: 12px;
  fontWeight: 700;
  color: #374151;
  textTransform: uppercase;
  letterSpacing: 0.5px;
  align-items: center;
`;

const AchievementRowContainer = styled.div<{ hasError?: boolean }>`
  display: grid;
  grid-template-columns: 190px 1fr 80px;
  align-items: flex-start;
  padding: 16px;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  marginBottom: 0px;
  width: 750px;
  min-height: ${props => props.hasError ? '88px' : '56px'};
`;

const InputCellWrapper = styled.div`
  display: flex;
  flex-direction: column;
  position: relative;
`;

const CurrencyInput = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const CurrencySymbol = styled.span`
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 14px;
  font-weight: 600;
  color: #64748b;
  z-index: 1;
`;

const CurrencyField = styled.input<{ hasError?: boolean }>`
  width: 100%;
  padding: 10px 12px 10px 32px;
  height: 36px;
  border: 1px solid ${props => props.hasError ? '#dc2626' : '#e2e8f0'};
  background-color: ${props => props.hasError ? '#fef2f2' : '#ffffff'};
  border-radius: 6px;
  font-size: 14px;
  color: #1e293b;
  transition: border-color 0.2s ease;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }

  &::placeholder {
    color: #9ca3af;
  }
`;

const IconColumn = styled.div`
  display: flex;
  gap: 8px;
  justify-content: flex-start;
  align-items: center;
  padding: 0;
`;

const IconButton = styled.button<{ variant: 'save' | 'cancel' }>`
  transition: all 0.2s ease;
  padding: 8px;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;

  ${props => props.variant === 'save' ? `
    background: #10b981;
    color: #ffffff;
    
    &:hover {
      background: #059669;
    }
    
    &:disabled {
      background: #dcfce7;
      color: #059669;
      cursor: not-allowed;
      opacity: 1;
    }
  ` : `
    background: #f1f5f9;
    color: #64748b;
    
    &:hover {
      background: #e2e8f0;
    }
  `}
`;

const formatCurrency = (value: string) => {
  const numericValue = value.replace(/[^0-9.]/g, '');
  if (numericValue === '') return '';

  const number = parseFloat(numericValue);
  if (isNaN(number)) return '';

  return number.toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  });
};

interface AchievementRowProps {
  achievement: RewardModel;
  existingAmounts: number[];
  borderRadius: string;
  onUpdate: (achievement: RewardModel) => void;
  onCancel: () => void;
  onDelete: () => void;
  existingLevels: any[];
  hideEditDelete?: boolean;
  onSave?: () => void;
  builderData?: any;
}

const AchievementRow: React.FC<AchievementRowProps> = ({
  achievement,
  existingAmounts,
  borderRadius,
  onUpdate,
  onCancel,
  onDelete,
  existingLevels,
  hideEditDelete = false,
  onSave,
  builderData
}) => {
  const [isEditing, setIsEditing] = useState(achievement.isEditing || false);
  const [tempLevel, setTempLevel] = useState(achievement.level?.toString() || '');
  const [tempReward, setTempReward] = useState(achievement.reward || '');
  const [rewardErrorMessage, setRewardErrorMessage] = useState('');
  const [amountErrorMessage, setAmountErrorMessage] = useState('');

  const isDuplicateAmount = () => {
    const amount = parseInt(tempLevel);
    if (!amount || isNaN(amount)) return false;
    return existingAmounts.filter(a => a !== achievement.level).includes(amount);
  };

  // Function to validate current achievement's amount against other achievements
  const validateCurrentAmount = () => {
    if (!tempLevel.trim()) return ''; // Empty is handled by basic validation

    const amount = parseInt(tempLevel);
    if (isNaN(amount)) return '';

    if (isDuplicateAmount()) {
      return 'Duplicate amount';
    }

    return ''; // No error
  };

  // Function to validate current achievement's reward name against other achievements
  const validateCurrentReward = () => {
    if (!tempReward.trim()) return ''; // Empty is handled by basic validation

    const allRewards = builderData?.rewards || [];

    // Check all other rewards for duplicate reward names
    for (let i = 0; i < allRewards.length; i++) {
      const otherReward = allRewards[i];
      if (otherReward && otherReward.id !== achievement.id) {
        const otherRewardName = otherReward.reward?.trim() || '';
        if (otherRewardName.toLowerCase() === tempReward.trim().toLowerCase()) {
          return 'Reward name must be unique';
        }
      }
    }

    return ''; // No error
  };

  const canSave = () => {
    const validation = performValidation();
    // Always allow saving - don't disable save button for validation errors
    return tempLevel.trim() !== '' && tempReward.trim() !== '';
  };

  const performValidation = () => {
    return {
      level: tempLevel.trim() === '',
      reward: tempReward.trim() === '',
      duplicateAmount: isDuplicateAmount()
    };
  };

  const handleSave = () => {
    const validation = performValidation();
    const amountValidation = validateCurrentAmount();
    const rewardValidation = validateCurrentReward();

    // Update error states
    setAmountErrorMessage(amountValidation);
    setRewardErrorMessage(rewardValidation);

    // Don't save if there are validation errors
    if (validation.level || validation.reward || validation.duplicateAmount || amountValidation || rewardValidation) return;

    onUpdate(new RewardModel({
      ...achievement,
      level: parseInt(tempLevel) || 0,
      reward: tempReward,
      isEditing: false
    }));
    setIsEditing(false);
    setAmountErrorMessage('');
    setRewardErrorMessage('');
    if (onSave) {
      onSave();
    }
  };

  const handleCancel = () => {
    setTempLevel(achievement.level?.toString() || '');
    setTempReward(achievement.reward || '');
    setIsEditing(false);
    setAmountErrorMessage('');
    setRewardErrorMessage('');
    if (achievement.isNew) {
      onCancel();
    }
  };

  const handleEdit = () => {
    setTempLevel(achievement.level?.toString() || '');
    setTempReward(achievement.reward || '');
    setIsEditing(true);
    setAmountErrorMessage('');
    setRewardErrorMessage('');
  };

  const handleAmountChange = (value: string) => {
    const cleanValue = value.replace(/[^0-9.]/g, '');
    setTempLevel(cleanValue);
    // Clear error when user starts typing
    if (amountErrorMessage) {
      setAmountErrorMessage('');
    }
  };

  const handleAmountBlur = () => {
    const amountValidation = validateCurrentAmount();
    setAmountErrorMessage(amountValidation);
  };

  const handleRewardChange = (value: string) => {
    setTempReward(value);
    // Clear error when user starts typing
    if (rewardErrorMessage) {
      setRewardErrorMessage('');
    }
  };

  if (isEditing) {
    return (
      <AchievementRowContainer style={{ borderRadius: borderRadius }} hasError={!!rewardErrorMessage || !!amountErrorMessage}>
        <InputCellWrapper>
          <CurrencyInput>
            <CurrencySymbol>$</CurrencySymbol>
            <CurrencyField
              type="text"
              placeholder="0"
              value={tempLevel ? formatCurrency(tempLevel) : ''}
              onChange={(e) => {
                handleAmountChange(e.target.value);
              }}
              onBlur={handleAmountBlur}
              hasError={!!amountErrorMessage}
              style={{
                width: '140px'
              }}
            />
          </CurrencyInput>
          {amountErrorMessage && (
            <div style={{
              fontSize: '12px',
              color: '#dc2626',
              fontWeight: '500',
              marginTop: '4px',
              textAlign: 'left',
              whiteSpace: 'nowrap',
              position: 'absolute',
              top: '38px',
              left: '0'
            }}>
              {amountErrorMessage}
            </div>
          )}
        </InputCellWrapper>
        <InputCellWrapper>
          <input
            type="text"
            value={tempReward}
            onChange={(e) => handleRewardChange(e.target.value)}
            maxLength={40}
            style={{
              width: '340px',
              padding: '10px 12px',
              height: '36px',
              boxSizing: 'border-box',
              border: `1px solid ${rewardErrorMessage ? '#dc2626' : '#e2e8f0'}`,
              backgroundColor: rewardErrorMessage ? '#fef2f2' : '#ffffff',
              borderRadius: '6px',
              fontSize: '14px'
            }}
            placeholder="Enter reward description..."
          />
          {rewardErrorMessage && (
            <div style={{
              fontSize: '12px',
              color: '#dc2626',
              fontWeight: '500',
              marginTop: '4px',
              textAlign: 'left',
              whiteSpace: 'nowrap',
              position: 'absolute',
              top: '38px',
              left: '0'
            }}>
              {rewardErrorMessage}
            </div>
          )}
        </InputCellWrapper>
        <IconColumn>
          <IconButton
            variant="save"
            type="button"
            onClick={handleSave}
          >
            <Check size={16} />
          </IconButton>
          <IconButton
            variant="cancel"
            type="button"
            onClick={handleCancel}
          >
            <X size={16} />
          </IconButton>
        </IconColumn>
      </AchievementRowContainer>
    );
  }

  return (
    <AchievementRowContainer style={{ borderRadius: borderRadius }}>
      <div>
        <span style={{ fontSize: '14px', fontWeight: '600', color: '#64748b' }}>$</span>
        <span style={{ fontSize: '14px', fontWeight: '600', color: '#1e293b' }}>
          {achievement.level?.toLocaleString() || '0'}
        </span>
      </div>
      <div style={{ fontSize: '14px', color: '#1e293b' }}>
        {achievement.reward || 'No reward description'}
      </div>
      <IconColumn style={{ display: hideEditDelete ? 'none' : 'flex' }}>
        <button
          type="button"
          onClick={handleEdit}
          style={{
            background: '#f1f5f9',
            border: 'none',
            color: '#64748b',
            padding: '8px',
            borderRadius: '6px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '32px',
            height: '32px'
          }}
        >
          <Pencil size={16} />
        </button>
        <button
          type="button"
          onClick={onDelete}
          style={{
            background: '#fee2e2',
            border: 'none',
            color: '#dc2626',
            padding: '8px',
            borderRadius: '6px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '32px',
            height: '32px'
          }}
        >
          <Trash2 size={16} />
        </button>
      </IconColumn>
    </AchievementRowContainer>
  );
};

interface MarketingSectionRewardsByAmountProps {
  builderData: CreateFundraiserModel;
  updateBuilderField: (field: keyof CreateFundraiserModel, value: any) => void;
  onSave?: () => void;
}

export const MarketingSectionRewardsByAmount: React.FC<MarketingSectionRewardsByAmountProps> = observer(({
  builderData,
  updateBuilderField,
  onSave
}) => {
  // Sort rewards by level descending on render
  const sortedRewards = (builderData.rewards || [])
    .slice()
    .sort((a: RewardModel, b: RewardModel) => (b.level || 0) - (a.level || 0));

  return (
    <>
      {sortedRewards.length > 0 && (
        <LevelsCaption>
          By Amount
        </LevelsCaption>
      )}

      {sortedRewards.length > 0 && (
        <AchievementsTableHeader>
          <div>AMOUNT</div>
          <div>REWARD</div>
          <div></div>
        </AchievementsTableHeader>
      )}

      {sortedRewards.map((reward: RewardModel, index: number) => {
        const existingAmounts = sortedRewards.map((a: RewardModel) => a.level).filter((level: number) => level !== undefined);
        const isLastRow = index === sortedRewards.length - 1 && sortedRewards.length > 1;
        const isSingleRow = sortedRewards.length === 1;

        let borderRadius;
        if (isSingleRow) {
          borderRadius = '0 0 8px 8px';
        } else if (isLastRow) {
          borderRadius = '0 0 8px 8px';
        } else {
          borderRadius = '0';
        }

        return (
          <AchievementRow
            key={reward.id}
            achievement={reward}
            existingAmounts={existingAmounts}
            borderRadius={borderRadius}
            builderData={builderData}
            onUpdate={(updatedReward: any) => {
              // Save the updated reward to builderData and localStorage
              let updated = sortedRewards.map((a: any) =>
                a.id === reward.id ? updatedReward : a
              );
              // Sort by level amount descending (highest first)
              updated = updated.sort((a: any, b: any) => (b.level || 0) - (a.level || 0));
              updateBuilderField('rewards', updated);
              if (onSave) {
                onSave();
              }
            }}
            onCancel={() => {
              if (reward.isNew) {
                const updated = sortedRewards.filter((a: any) => a.id !== reward.id);
                updateBuilderField('rewards', updated);
              }
            }}
            onDelete={() => {
              const updated = sortedRewards.filter((a: any) => a.id !== reward.id);
              updateBuilderField('rewards', updated);
              if (onSave) {
                onSave();
              }
            }}
            existingLevels={[]}
            onSave={onSave}
          />
        );
      })}

      {/* Show + Level button only when rewards exist */}
      {sortedRewards.length > 0 && (
        <div style={{ display: 'flex', justifyContent: 'flex-start', gap: '16px', marginTop: '16px' }}>
          <AddLinkButton
            type="button"
            onClick={() => {
              const newReward = {
                id: Date.now().toString(),
                level: '',
                reward: '',
                isEditing: true,
                isNew: true
              };
              updateBuilderField('rewards', [...sortedRewards, newReward]);
            }}
          >
            + Level
          </AddLinkButton>
        </div>
      )}
    </>
  );
});